// Shared helpers for postcard template patterns (postcard_pattern_type JSON).
// The JSON shape is rendered server-side by PostcardGenerationService, so the
// fields here must stay in sync with it.

import restApi from '@/api/index'

export const QR_TOKEN = '{unique_url_qr}'

export const PATTERN_VARIABLES = [
	{ token: '{name}', description: 'Recipient name' },
	{ token: '{address}', description: 'Mailing address' },
	{ token: '{city}', description: 'City' },
	{ token: '{state}', description: 'State abbreviation' },
	{ token: '{zip}', description: 'ZIP code' },
	{ token: '{unique_url}', description: 'Unique tracked link for this recipient' },
	{ token: QR_TOKEN, description: 'QR code that opens the unique tracked link' },
]

const SAMPLE_VALUES = {
	'{name}': 'City of Sample',
	'{address}': '123 Main Street',
	'{city}': 'Anytown',
	'{state}': 'NY',
	'{zip}': '10001',
	'{country}': 'US',
	'{description}': '',
	// Printed short link; the QR code opens the same link with a trailing "q".
	'{unique_url}': 'twn.link/a7k2m',
}

export function patternPreviewText(text) {
	return Object.entries(SAMPLE_VALUES).reduce((value, [token, sample]) => value.split(token).join(sample), text || '')
}

/**
 * Fonts a template text pattern can use come from mc2-api (GET /template-fonts), which lists the
 * folders under storage/fonts that PostcardGenerationService renders with. Uploads happen in
 * Settings → Template Fonts. `value` is stored in pattern.fontFamily.
 */
export const DEFAULT_TEMPLATE_FONTS = [{ value: 'PlusJakartaSans', label: 'Plus Jakarta Sans', is_default: true, files: [] }]

let templateFontsRequest = null

/** Template font families (cached; pass force after an upload/delete). */
export function fetchTemplateFonts(force = false) {
	if (force || !templateFontsRequest) {
		templateFontsRequest = restApi
			.get('/template-fonts')
			.then(({ data }) => (data.fonts && data.fonts.length ? data.fonts : DEFAULT_TEMPLATE_FONTS))
			.catch(error => {
				console.warn('Could not load template fonts', error)
				templateFontsRequest = null
				return DEFAULT_TEMPLATE_FONTS
			})
	}
	return templateFontsRequest
}

export function previewFontFamily(fontFamily) {
	// Google's Plus Jakarta Sans stands in while the server TTFs load.
	return fontFamily === 'PlusJakartaSans' ? 'PlusJakartaSans, "Plus Jakarta Sans"' : fontFamily
}

/** Weight 900 ("Black") is ExtraBold plus a same-color stroke; mirrors PostcardGenerationService::BLACK_STROKE_RATIO. */
export const BLACK_STROKE_RATIO = 0.01

/** CSS -webkit-text-stroke for the preview, or null when the pattern isn't Black. */
export function patternTextStroke(pattern, displayFontSize) {
	if (Number(pattern.fontWeight) < 900) return null
	const fontSize = pattern.fontSize || 48
	// The server stamps the text within a whole-pixel radius; a centered CSS stroke needs twice that width.
	const radius = Math.max(1, Math.round(fontSize * BLACK_STROKE_RATIO))
	return `${((radius * 2 * displayFontSize) / fontSize).toFixed(2)}px ${pattern.color || '#000000'}`
}

/** Measure a text pattern with canvas; the server uses these for alignment. */
export function measurePattern(pattern) {
	const ctx = document.createElement('canvas').getContext('2d')
	const fontSize = pattern.fontSize || 48
	ctx.font = `${pattern.fontWeight || '400'} ${fontSize}px ${previewFontFamily(pattern.fontFamily || 'PlusJakartaSans')}`
	pattern.patternWidth = ctx.measureText(pattern.text).width
	pattern.patternHeight = fontSize * 1.2
	return pattern
}

export function newPattern(text, x, y) {
	const isQr = text === QR_TOKEN
	return measurePattern({
		text,
		x: Math.min(Math.max(x, 0), 95),
		y: Math.min(Math.max(y, 0), 95),
		fontSize: 48,
		color: '#000000',
		fontFamily: 'PlusJakartaSans',
		fontWeight: '400',
		qrSize: isQr ? 220 : null,
		coordinateVersion: 2,
		patternHeight: 0,
		patternWidth: 0,
	})
}

/** postcard_pattern_type JSON → { front: [], back: [] } editor patterns. */
export function parsePatterns(json) {
	const sides = { front: [], back: [] }
	if (!json) return sides

	let all = []
	try {
		all = JSON.parse(json) || []
	} catch (error) {
		console.error('Error parsing postcard_pattern_type JSON:', error)
		return sides
	}

	all.forEach(pattern => {
		if (!sides[pattern.image]) return
		const data = {
			text: pattern.text,
			x: pattern.position.x,
			y: pattern.position.y,
			fontSize: pattern.fontSize || 18,
			fontWeight: pattern.fontWeight || '400',
			qrSize: pattern.qrSize || (pattern.text === QR_TOKEN ? 220 : null),
			coordinateVersion: pattern.coordinateVersion || 1,
			color: pattern.color || '#000000',
			fontFamily: pattern.fontFamily || 'PlusJakartaSans',
			patternHeight: pattern.patternHeight || 0,
			patternWidth: pattern.patternWidth || 0,
		}
		sides[pattern.image].push(data.patternWidth && data.patternHeight ? data : measurePattern(data))
	})

	return sides
}

/** { front, back } editor patterns → postcard_pattern_type array (null when empty). */
export function buildPatterns(front = [], back = []) {
	const toJson = side => (pattern, index) => ({
		image: side,
		index,
		text: pattern.text,
		position: { x: pattern.x, y: pattern.y },
		fontSize: pattern.fontSize,
		fontWeight: pattern.fontWeight,
		qrSize: pattern.qrSize,
		coordinateVersion: pattern.coordinateVersion || 1,
		color: pattern.color,
		fontFamily: pattern.fontFamily,
		patternHeight: pattern.patternHeight,
		patternWidth: pattern.patternWidth,
	})

	return [...front.map(toJson('front')), ...back.map(toJson('back'))]
}

export function patternsUseUniqueUrl(patterns) {
	return patterns.some(pattern => (pattern.text || '').includes('{unique_url'))
}

const loadedFontUrls = new Set()

/**
 * Load the TTFs the server renders with, so the editor preview matches print.
 * Resolves to the template font list (for the Font Family select).
 */
export async function loadTemplateFonts(force = false) {
	const fonts = await fetchTemplateFonts(force)
	if (!document.fonts) return fonts

	const files = fonts.flatMap(font => (font.files || []).map(file => ({ family: font.value, ...file })))
	await Promise.all(
		files
			.filter(file => !loadedFontUrls.has(file.url))
			.map(async file => {
				try {
					const face = new FontFace(file.family, `url(${file.url})`, { weight: String(file.weight), style: file.italic ? 'italic' : 'normal' })
					await face.load()
					document.fonts.add(face)
					loadedFontUrls.add(file.url)
				} catch (error) {
					console.warn(`Could not load ${file.name} for template preview`, error)
				}
			})
	)
	try {
		await document.fonts.load('16px "Plus Jakarta Sans"')
	} catch (error) {
		console.warn('Could not load Google Plus Jakarta Sans fallback', error)
	}
	return fonts
}
